import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { MaskedLines, Reveal, RevealWords } from "@/components/site/Reveal";
import { EditorialMarquee } from "@/components/site/Marquee";
import { AmazonButton } from "@/components/site/ProductCard";
import { IMAGES, AMAZON_STORE } from "@/data/site";

const verses = [
  {
    n: "I.",
    title: "It Began With Love",
    body: "Fashiella began with a mother's love and a dream to create something beautiful for her daughter. In the quiet ritual of brushing hair and tying a ribbon, something tender took shape — a wish to hold onto the little moments that make a day feel whole.",
  },
  {
    n: "II.",
    title: "A Love For Beauty",
    body: "Inspired by the little moments of joy that come from styling hair, dressing up, and celebrating everyday beauty, Fashiella was created to bring elegant and timeless hair accessories to women and girls who appreciate thoughtful details.",
  },
  {
    n: "III.",
    title: "Attention To Detail",
    body: "Every accessory represents love, creativity, and attention to detail. Each stitch, each pearl, each fold of satin is considered — because we believe the smallest details create the most beautiful impressions.",
  },
  {
    n: "IV.",
    title: "Everyday Moments",
    body: "From everyday hairstyles to special occasions, Fashiella celebrates the beautiful connection between memories, confidence and personal style — accessories made to be worn, loved, and remembered.",
  },
];

export default function Story() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-40, 60]);

  return (
    <>
      <section className="mx-auto max-w-7xl px-5 sm:px-8 pt-40 pb-20" data-testid="story-hero">
        <p className="text-xs uppercase tracking-[0.35em] text-rosegold mb-7">Our Story</p>
        <h1 className="font-serif text-5xl sm:text-7xl lg:text-[7rem] leading-[0.9] tracking-tight max-w-4xl">
          <MaskedLines lines={["A Mother's Love,"]} delay={0.2} />
          <MaskedLines lines={["A Beautiful"]} delay={0.34} />
          <MaskedLines lines={["Beginning."]} delay={0.46} wordClass="italic text-rosegold" />
        </h1>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-10 grid lg:grid-cols-12 gap-8">
        <Reveal className="lg:col-span-7" delay={0.1}>
          <div ref={ref} className="relative overflow-hidden aspect-[16/11]">
            <motion.img style={{ y }} src={IMAGES.storyMain} alt="Mother and daughter" className="absolute inset-0 h-[120%] w-full object-cover" />
          </div>
        </Reveal>
        <div className="lg:col-span-5 flex items-center">
          <Reveal delay={0.15}>
            <p className="font-serif text-3xl sm:text-4xl leading-snug tracking-tight">
              <RevealWords text="Where memory, confidence and personal style are gently woven together." />
            </p>
          </Reveal>
        </div>
      </section>

      <EditorialMarquee items={["Created With Love", "Made By Hand", "Worn With Confidence"]} className="my-16" />

      <section className="mx-auto max-w-4xl px-5 sm:px-8 py-10" data-testid="story-verses">
        {verses.map((v, i) => (
          <Reveal key={v.n} delay={i * 0.05}>
            <div className="py-14 border-b border-black/10 grid sm:grid-cols-12 gap-6">
              <span className="sm:col-span-2 font-serif text-4xl text-rosegold/70">{v.n}</span>
              <div className="sm:col-span-10">
                <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mb-4">{v.title}</h2>
                <p className="text-charcoal/60 leading-relaxed text-lg">{v.body}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-24 grid lg:grid-cols-2 gap-6">
        <Reveal>
          <div className="relative overflow-hidden aspect-[4/5]">
            <img src={IMAGES.storySub} alt="Elegant accessory detail" className="h-full w-full object-cover" />
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="h-full flex flex-col justify-center bg-blush/40 px-8 sm:px-14 py-16">
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight leading-tight">
              Every piece, <span className="italic text-rosegold">a little moment</span> of joy.
            </h2>
            <p className="mt-7 text-charcoal/60 leading-relaxed text-lg">
              We hope Fashiella finds its way into your everyday rituals and your most treasured occasions —
              and that every time you wear it, you feel a little more beautiful.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <AmazonButton href={AMAZON_STORE} label="Shop on Amazon" testid="story-amazon-btn" />
              <Link to="/catalogue" data-testid="story-collections-link" className="group inline-flex items-center gap-2 rounded-full border border-charcoal px-7 py-3 text-xs uppercase tracking-[0.2em] transition-colors duration-400 hover:bg-charcoal hover:text-ivory">
                Explore Catalogue
                <ArrowUpRight size={15} className="transition-transform duration-400 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </>
  );
}
